import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Account, UserPhotoLimitInfo } from '@photomaton/shared';

interface AuthContextValue {
  account: Account | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  login: () => void;
  loginWithCode: (name: string, code: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  photoLimitInfo: UserPhotoLimitInfo | null;
  refreshPhotoLimitInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoLimitInfo, setPhotoLimitInfo] = useState<UserPhotoLimitInfo | null>(null);

  const isAuthenticated = account !== null;
  const isAdmin = account?.role === 'admin';

  /**
   * Fetch current user from backend
   */
  const fetchAccount = useCallback(async () => {
    try {
      const response = await fetch('/auth/me', {
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        const data = await response.json();
        setAccount(data);
        setError(null);
      } else if (response.status === 401) {
        // Not authenticated
        setAccount(null);
      } else {
        throw new Error('Failed to fetch account');
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initiate login (redirect to OAuth)
   */
  const login = useCallback(() => {
    const currentPath = window.location.pathname;
    window.location.href = `/auth/login?redirect=${encodeURIComponent(currentPath)}`;
  }, []);

  /**
   * Login with name and code
   */
  const loginWithCode = useCallback(async (name: string, code: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/auth/login/code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, code }),
      });

      if (response.ok) {
        const data = await response.json();
        setAccount(data.account);
        setError(null);
        return true;
      } else {
        const errorData = await response.json();
        setError(errorData.error?.message || 'Invalid name or code');
        return false;
      }
    } catch (err: any) {
      console.error('Code login error:', err);
      setError(err.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setAccount(null);
      window.location.href = '/login';
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message);
    }
  }, []);

  /**
   * Refresh account data
   */
  const refreshAccount = useCallback(async () => {
    await fetchAccount();
  }, [fetchAccount]);

  /**
   * Fetch user's photo limit information
   */
  const fetchPhotoLimitInfo = useCallback(async () => {
    try {
      const response = await fetch('/auth/me/limits', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPhotoLimitInfo(data);
      } else if (response.status === 401) {
        setPhotoLimitInfo(null);
      }
    } catch (err) {
      console.error('Failed to fetch photo limit info:', err);
    }
  }, []);

  /**
   * Refresh photo limit info (call after capture or deletion)
   */
  const refreshPhotoLimitInfo = useCallback(async () => {
    await fetchPhotoLimitInfo();
  }, [fetchPhotoLimitInfo]);

  // Fetch account on mount
  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  // Fetch photo limit info when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPhotoLimitInfo();
    } else {
      setPhotoLimitInfo(null);
    }
  }, [isAuthenticated, fetchPhotoLimitInfo]);

  // Handle OAuth callback errors
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        auth_failed: 'Authentication failed. Please try again.',
        invalid_domain: 'Only @group-era.com email addresses are allowed.',
        invalid_state: 'Invalid authentication state. Please try again.',
        missing_code: 'Authentication code missing. Please try again.',
        session_error: 'Session error occurred. Please try again.',
      };

      setError(errorMessages[errorParam] || 'An error occurred during authentication.');

      // Clear error from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const value: AuthContextValue = {
    account,
    isAuthenticated,
    isAdmin,
    loading,
    error,
    login,
    loginWithCode,
    logout,
    refreshAccount,
    photoLimitInfo,
    refreshPhotoLimitInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
